import { partition } from 'lodash';
import { getElementId } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';
import { Label } from '../../../../../../common/labels/labelTypes';
import {
    BoardVirtualPathMap,
    LabeledElementsData,
} from '../../../../../../common/api/labels/labelElementsRetrievalRouteTypes';

export const isLabelMultiContext = (label: Label): boolean => label.rootBoardIds?.length > 1;

// Find the id of the board in the current share context that the label is associated with
// It will return the lowest level board id in the shareRootPath that matches one of the label's rootBoardIds
export const getCurrentContextRootId = (label: Label, shareRootPath: ImMNElement[]): string | undefined => {
    // Search from the bottom of the share path to find the closest matching rootBoardId
    const boardIds = shareRootPath.map((element) => getElementId(element)).reverse();
    return label.rootBoardIds.find((id) => boardIds.includes(id));
};

export const getHighestLabelBoardId = (label: Label, userRootPath: ImMNElement[]): string => {
    if (!isLabelMultiContext(label)) {
        return label.rootBoardIds[0];
    }

    for (const board of userRootPath) {
        const id = getElementId(board);
        if (label.rootBoardIds.includes(id)) {
            return id;
        }
    }

    return label.rootBoardIds[0];
};

// Check if the current label root is used for applying the label on any element within the current context
export const isLabelRootUsed = (
    labeledElements: LabeledElementsData[],
    contextRootsInCurrentPath: string[],
    labelRootId: string,
    boardVirtualPathToRootMap: BoardVirtualPathMap,
): boolean => {
    return labeledElements.some((el) => {
        if (!el.elementParentId) return false;

        const elementPath = boardVirtualPathToRootMap[el.elementParentId];
        const contextRoot = elementPath.find((boardId) => contextRootsInCurrentPath.includes(boardId));

        for (let i = 0; i < elementPath.length; i++) {
            const boardId = elementPath[i];
            if (boardId === labelRootId) return true;
            if (boardId === contextRoot) return false;
        }
    });
};

// Returns 2 lists of label roots split by whether they sit under the current editing label or not, which determines whether they can be updated
export const partitionLabelRootsByEditability = (
    currentlyEditingLabel: Label,
    userRootPath: ImMNElement[],
    boardVirtualPathToRootMap: BoardVirtualPathMap,
) => {
    const highestRootId = getHighestLabelBoardId(currentlyEditingLabel, userRootPath);
    const [labelRootsToUpdate, labelRootsNotToUpdate] = partition(currentlyEditingLabel.rootBoardIds, (rootBoardId) => {
        const pathToRoot = boardVirtualPathToRootMap?.[rootBoardId];
        return pathToRoot && pathToRoot.includes(highestRootId);
    });

    return { labelRootsToUpdate, labelRootsNotToUpdate };
};

export const getElementIdsForLabelUpdate = (
    labeledElements: LabeledElementsData[],
    editingRootId: string,
    boardVirtualPathToRootMap: BoardVirtualPathMap | null,
) => {
    return labeledElements
        .filter((el) => {
            if (!el.elementParentId) return false;

            const pathToRoot = boardVirtualPathToRootMap?.[el.elementParentId];
            return pathToRoot && pathToRoot.includes(editingRootId);
        })
        .map((el) => el.elementId);
};
